import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { LegacyRow } from '../shared';

export type Field = { name: string; label: string; multiline?: boolean; rows?: number; required?: boolean; readOnly?: boolean; allowBlank?: boolean; checkbox?: 'boolean' | 'legacy-truthy'; choices?: { value: string; label: string }[] };
export type Result = { success?: boolean; status?: number; message?: string | null; id?: string | null };
export const savedResult = (result: Result | null | undefined) => {
  if (!result?.success || Number(result.status ?? 200) >= 400) throw new Error(result?.message || 'The change could not be saved.');
};
export const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'The operation could not be completed.';
export const choices = (values: string[]) => values.map(value => ({ value, label: value }));

export { legacyText as decodeLegacyText } from '../shared';

export const RecordForm = ({ id, fields, values, onSave, onCancel, submitLabel = 'Save', resetOnSave = false }: {
  id: string; fields: Field[]; values?: LegacyRow; onSave: (values: Record<string, string>) => Promise<void>; onCancel?: () => void; submitLabel?: string; resetOnSave?: boolean;
}) => {
  const defaults = Object.fromEntries(fields.map(field => {
    const value = values?.[field.name] ?? '';
    if (!field.choices || field.choices.some(option => option.value === value)) return [field.name, value];
    // Native select elements choose their first option when the old value does
    // not occur in the option list; do not manufacture a new historical option.
    return [field.name, field.allowBlank === false ? field.choices[0]?.value ?? '' : ''];
  }));
  const { register, control, handleSubmit, reset, formState: { isSubmitting, errors } } = useForm<Record<string, string>>({ defaultValues: defaults });
  const [error, setError] = useState('');
  return <form id={id} onSubmit={handleSubmit(async input => {
    setError('');
    try {
      await onSave(input);
      if (resetOnSave) reset(defaults);
    } catch (caught) { setError(errorMessage(caught)); }
  })} className="space-y-3">
    {fields.map(field => <div key={field.name}>
      <label htmlFor={`${id}-${field.name}`} className="mb-1 block text-sm font-medium">{field.label}</label>
      {field.choices && <Controller name={field.name} control={control} rules={{ required: field.required }} render={({ field: controlField }) => <Select value={controlField.value || '__blank__'} onValueChange={value => controlField.onChange(value === '__blank__' ? '' : value)}>
        <SelectTrigger id={`${id}-${field.name}`}><SelectValue placeholder="Not selected" /></SelectTrigger>
        <SelectContent>
          {field.allowBlank !== false && <SelectItem id={`${id}-${field.name}-blank`} value="__blank__">Not selected</SelectItem>}
          {(field.choices ?? []).filter(option => option.value !== '').map((option, index) => <SelectItem id={`${id}-${field.name}-option-${index}`} key={`${option.value}-${index}`} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>} />}
      {field.checkbox && <Controller name={field.name} control={control} render={({ field: controlField }) => <Checkbox id={`${id}-${field.name}`} checked={field.checkbox === 'legacy-truthy' ? Boolean(controlField.value) : controlField.value === '1'} onCheckedChange={checked => controlField.onChange(checked === true ? '1' : field.checkbox === 'legacy-truthy' ? '' : '0')} />} />}
      {!field.choices && !field.checkbox && field.multiline && <Textarea id={`${id}-${field.name}`} rows={field.rows ?? 4} {...register(field.name, { required: field.required })} />}
      {!field.choices && !field.checkbox && !field.multiline && <Input id={`${id}-${field.name}`} readOnly={field.readOnly} {...register(field.name, { required: field.required })} />}
      {errors[field.name] && <p role="alert" className="text-sm text-[var(--error-color)]">This field is required.</p>}
    </div>)}
    {error && <p role="alert" className="text-sm text-[var(--error-color)]">{error}</p>}
    <div className="flex justify-end gap-2">
      <Button id={`${id}-save`} type="submit" disabled={isSubmitting}><Save className="h-4 w-4" />{isSubmitting ? 'Saving…' : submitLabel}</Button>
      {onCancel && <Button id={`${id}-cancel`} type="button" variant="outline" onClick={onCancel}>Cancel</Button>}
    </div>
  </form>;
};
